import { Mic, MicOff, AlertTriangle, HelpCircle, Sparkles, Check, X, GraduationCap, Radio, Headphones, Gauge } from "lucide-react";
import { useEffect, useState } from "react";
import { useWhisperSTT } from "../../hooks/useWhisperSTT";
import { extendSttAliases } from "../../hooks/useVoiceScoring";
import { useVoiceI18n } from "../../lib/voiceI18n";
import api from "../../api/axios";


// Help-modal sub-components. Local to this file to keep the cheat sheet
// markup compact (every Section is a heading + list of kbd rows).
function Section({ title, children }) {
  return (
    <div className="mb-3">
      <div className="text-[10px] uppercase tracking-wider text-purple-300 font-semibold mb-1.5">
        {title}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
        {children}
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-start gap-2">
      <kbd className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-bg-secondary border border-border-default text-text-primary shrink-0 max-w-[55%] whitespace-nowrap overflow-hidden text-ellipsis">
        {k}
      </kbd>
      <span className="text-text-secondary leading-tight">{v}</span>
    </div>
  );
}

export default function VoiceScoringPanel({
  voice,
  flat = false,
  // AI fallback toggle — controlled by parent (BBP) so the parent can
  // persist to localStorage. Renders a small ✨ button next to the mic
  // when defined; omitted entirely if parent doesn't pass the props
  // (no-op for callers that haven't wired it).
  aiFallbackEnabled = undefined,
  onAiFallbackChange = undefined,
  // Re-trigger for the first-time voice tutorial. If provided, renders
  // a 🎓 "Tour" button next to the help icon that re-opens the
  // walk-through (handy when an operator dismissed it too quickly or
  // a new team-mate borrows the same laptop).
  onReplayTour = undefined,
  // Run the 60-sec calibration gauntlet (founder ask 2026-05-26 #13).
  onCalibrate = undefined,
  // Match ID — passed to Whisper PTT so the backend can inject the
  // match's team rosters into the prompt for vocab biasing.
  matchId = null,
  // Optional: parent callback called with each utterance ID Groq
  // returns. Parent collects these into a per-ball buffer and POSTs
  // to /api/scoring/voice/link/ on ball save.
  onUtterance = null,
}) {
  const {
    supported, listening, transcript, lastApplied, recentApplied, error, toggle,
    pendingApply, confirmPending, cancelPending,
    applyTranscript,
  } = voice;

  // Push-to-talk Whisper button — high-accuracy alternative to the
  // continuous browser STT. Krishna's team rated browser STT 3/10
  // (2026-05-26); Whisper via Groq is ~95% on Indian English cricket
  // vocab. Operator picks which feels better; PTT is recommended.
  const { lang, setLang, t } = useVoiceI18n();
  const ptt = useWhisperSTT({
    onTranscript: (text) => applyTranscript?.(text),
    matchId,
    onUtterance,
  });

  // Hold "V" key globally to record via Whisper PTT. Unified hotkey
  // 2026-05-26 — V on BBP used to toggle continuous browser STT,
  // confusing operators who learned V-hold on the form pages
  // (Toss / XI / Squad / Add). Now V means "hold to talk" everywhere.
  // The continuous browser STT button is still clickable for fallback
  // but no longer responds to keyboard.
  useEffect(() => {
    if (!ptt.supported) return undefined;
    let pressed = false;
    const isFormField = (el) => {
      const tag = (el?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return true;
      if (el?.isContentEditable) return true;
      return false;
    };
    // 2026-06-18 — when a modal dialog is open (e.g. the calibration
    // gauntlet), it owns the V hotkey. Without this check BOTH listeners
    // fired startRecording on the same key press, spinning up two
    // MediaRecorders on the shared mic stream — one captured audio, the
    // other captured silence, and Whisper hallucinated on the silent
    // one. Caused 100% rejection of voice in today's gauntlet runs.
    const isModalOpen = () => !!document.querySelector('[role="dialog"][aria-modal="true"]');

    const onDown = (e) => {
      if ((e.key || "").toLowerCase() !== "v") return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (isFormField(e.target)) return;
      if (isModalOpen()) return;
      // Shift+V = repeat last utterance through Whisper (no re-speak).
      // Founder ask 2026-05-26: "Repeat that" for one-off STT slips.
      if (e.shiftKey) {
        if (e.repeat) return;
        e.preventDefault();
        ptt.repeatLast?.();
        return;
      }
      if (e.repeat) return;
      pressed = true;
      e.preventDefault();
      ptt.startRecording();
    };
    const onUp = (e) => {
      if ((e.key || "").toLowerCase() !== "v") return;
      if (!pressed) return;
      pressed = false;
      e.preventDefault();
      ptt.stopRecording();
    };
    window.addEventListener("keydown", onDown);
    window.addEventListener("keyup", onUp);
    return () => {
      window.removeEventListener("keydown", onDown);
      window.removeEventListener("keyup", onUp);
    };
  }, [ptt.supported, ptt.startRecording, ptt.stopRecording]);
  const [helpOpen, setHelpOpen] = useState(false);
  const showAiToggle = typeof aiFallbackEnabled === "boolean" && onAiFallbackChange;

  // Countdown ticker for the pending-apply toast. Fires every 100ms
  // while pendingApply is non-null so the "2s left" timer feels live.
  const [pendingNow, setPendingNow] = useState(Date.now());
  useEffect(() => {
    if (!pendingApply) return undefined;
    const id = setInterval(() => setPendingNow(Date.now()), 100);
    return () => clearInterval(id);
  }, [pendingApply]);
  const pendingMsLeft = pendingApply
    ? Math.max(0, pendingApply.expiresAt - pendingNow)
    : 0;

  // T126: fetch server-mined STT aliases on first mount (within a
  // session). The backend derives the dict from operator corrections,
  // so a heard→intended mistake that recurs becomes a global alias
  // without a code change. Cached for 12h in localStorage so we don't
  // hammer the endpoint between page loads. Failure is silent — alias
  // dict still works from the hand-curated baseline.
  // 2026-05-29 — TWO alias fetches now, layered:
  //   1. Global (every operator's corrections; min_count=3, 30d window;
  //      cached 12h). Slow-moving baseline of widely-confirmed
  //      mishearings.
  //   2. Personal (user_filter=me; min_count=1, 365d window; cached 1h).
  //      Your own calibration-gauntlet pairs + post-save corrections —
  //      applied immediately, no need to wait for other operators to
  //      hit the same mishearing.
  // Both layer onto the hand-curated baseline. Personal overrides
  // global if both have the same `heard` key.
  useEffect(() => {
    let cancelled = false;
    const GLOBAL_KEY = "zcd_voice_aliases_v1";
    const PERSONAL_KEY = "zcd_voice_aliases_personal_v1";
    const GLOBAL_TTL = 12 * 60 * 60 * 1000;  // 12h
    const PERSONAL_TTL = 60 * 60 * 1000;     // 1h — fresh calibration data lands fast

    const loadCached = (key, ttl) => {
      try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const { ts, data } = JSON.parse(raw);
        if (ts && Date.now() - ts < ttl && data && typeof data === "object") {
          return data;
        }
      } catch { /* corrupt cache */ }
      return null;
    };

    const fetchAndCache = async (url, key) => {
      try {
        const r = await api.get(url);
        if (cancelled) return null;
        const aliases = r?.data?.aliases || {};
        try {
          localStorage.setItem(key, JSON.stringify({ ts: Date.now(), data: aliases }));
        } catch { /* quota — fine */ }
        return aliases;
      } catch {
        return null;
      }
    };

    // Layer 1 — global (cached 12h).
    (async () => {
      const cached = loadCached(GLOBAL_KEY, GLOBAL_TTL);
      const aliases = cached ?? await fetchAndCache(
        "/api/scoring/voice/aliases/?min_count=3&since_days=30",
        GLOBAL_KEY,
      );
      if (aliases) extendSttAliases(aliases);
    })();

    // Layer 2 — personal (cached 1h). user_filter=me + min_count=1 so
    // calibration-gauntlet pairs surface on the next session start.
    (async () => {
      const cached = loadCached(PERSONAL_KEY, PERSONAL_TTL);
      const aliases = cached ?? await fetchAndCache(
        "/api/scoring/voice/aliases/?user_filter=me&min_count=1&since_days=365",
        PERSONAL_KEY,
      );
      if (aliases) {
        const added = extendSttAliases(aliases);
        if (added > 0) {
          // eslint-disable-next-line no-console
          console.info(`Loaded ${added} personalized STT aliases from your corrections.`);
        }
      }
    })();

    return () => { cancelled = true; };
  }, []);

  // 2026-05-26: removed the legacy V-toggles-continuous-STT hotkey.
  // V is now reserved for Whisper PTT hold-to-talk everywhere in the
  // app (see the PTT useEffect above). Continuous browser STT is
  // still accessible via the "Voice" button click but no longer
  // listens for keyboard events — fewer surprises when operators
  // press V expecting hold-to-talk and get a continuous-mic toggle.

  // flex-wrap so the 6+ controls in this row (Voice + Hands-free + PTT
  // + AI + Tutorial + Help + transcript readout) gracefully wrap on
  // narrow viewports instead of overflowing off-screen. Founder UI
  // audit 2026-05-26.
  // Sprint 4 (2026-05-27): voice-related buttons bump to 44px touch
  // target on phone via `[&_button]:min-h-[44px] sm:[&_button]:min-h-0`
  // so the mic / hands-free / Whisper / help / tour controls all stay
  // tappable without zooming. sm+ keeps the dense action-bar density.
  const wrapperCls = flat
    ? "flex flex-wrap items-center gap-2 text-xs [&_button]:min-h-[44px] sm:[&_button]:min-h-0"
    : "bg-bg-primary border border-bg-secondary rounded-lg px-2 py-1 flex flex-wrap items-center gap-2 text-xs [&_button]:min-h-[44px] sm:[&_button]:min-h-0";
  return (
    <div className={wrapperCls}>
      {/* Continuous browser-STT button removed 2026-05-26. It had a
          well-known Web Speech deactivation bug (recognition stops
          after ~30-60s of silence and silently fails to restart) and
          its accuracy was the bottleneck Whisper PTT replaces. The
          underlying `toggle` / `listening` state is kept in the hook
          for backwards compatibility with any code that still reads
          it, but the UI entry point is gone. Operators use PTT (hold
          V) or hands-free instead. */}

      {/* Hands-free toggle removed 2026-05-27 per founder ask. PTT
          (push-to-talk Whisper, below) remains the primary voice
          entry point. The toggleHandsFree handler stays on the hook
          for any downstream consumer that still reads it, but the
          UI affordance is gone. */}

      {/* PTT Whisper — primary voice entry point. Hold V to record.
          2026-05-28: switched from separate mouse + touch handlers to
          unified Pointer Events + pointer capture. The old setup had
          two bugs reported by the founder:
            1) onMouseLeave fired the moment the operator's finger /
               cursor drifted off the button while still pressing —
               recording stopped mid-utterance.
            2) onTouchStart + onMouseDown could double-fire on
               touchscreens (synthetic mouse events after touch), so
               startRecording ran twice and the second call replaced
               the active recorder.
          With setPointerCapture, the pointerup is delivered to the
          button regardless of where the finger / cursor is when
          released, so drift no longer ends recording. Pointer events
          don't synthesize mouse events, so no double-fire. */}
      {/* PTT error / "Didn't catch that" badge. Renders ptt.error
          inline next to the mic so the operator actually sees when
          Whisper rejected silent/hallucinated audio. Auto-clears on
          the next recording press (useWhisperSTT.startRecording
          calls setError("")). Critical addition 2026-06-18: without
          this, the no_speech_detected toast set by the transcribe
          handler was invisible on BBP and the operator perceived
          voice scoring as "nothing happened". */}
      {ptt.supported && ptt.error && (
        <span
          className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-md border border-rose-500/50 bg-rose-500/15 text-rose-200 max-w-[260px]"
          title={ptt.error}
          role="status"
          aria-live="polite"
        >
          <AlertTriangle className="w-3 h-3 shrink-0" />
          <span className="truncate">{ptt.error}</span>
        </span>
      )}

      {ptt.supported && (
        <button
          type="button"
          onPointerDown={(e) => {
            e.preventDefault();
            try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ }
            ptt.startRecording();
          }}
          onPointerUp={(e) => {
            try { if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
            ptt.stopRecording();
          }}
          onPointerCancel={(e) => {
            try { if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
            if (ptt.recording) ptt.stopRecording();
          }}
          onContextMenu={(e) => e.preventDefault()}
          aria-pressed={!!ptt.recording}
          title={
            ptt.recording ? "Release to transcribe" :
            ptt.transcribing ? "Transcribing audio via Groq Whisper…" :
            "Hold V (or this button) to record — Whisper STT, higher accuracy"
          }
          className={`relative overflow-hidden flex items-center gap-1.5 px-2 py-1 rounded-md font-semibold text-xs touch-manipulation transition-colors select-none ${
            ptt.recording ? "bg-red-500/30 border border-red-500/60 text-red-200" :
            ptt.transcribing ? "bg-amber-500/20 border border-amber-500/50 text-amber-300" :
            "bg-purple-500/15 border border-purple-500/40 text-purple-200 hover:bg-purple-500/30"
          }`}
        >
          {/* Live audio-level meter — operator can see mic is hot.
              Fills the button background L→R based on RMS level.
              Founder ask 2026-05-26 ("can't tell if it's recording"). */}
          {ptt.recording && (
            <span
              aria-hidden
              className="absolute left-0 top-0 bottom-0 bg-red-400/40 transition-[width] duration-75 pointer-events-none"
              style={{ width: `${Math.round((ptt.audioLevel || 0) * 100)}%` }}
            />
          )}
          <Radio className={`w-3.5 h-3.5 relative ${ptt.recording ? "animate-pulse" : ""}`} />
          <span className="uppercase tracking-wider text-[10px] relative">
            {ptt.recording ? t("voice.recording") : ptt.transcribing ? t("voice.parsing") : t("voice.hold_to_talk")}
          </span>
          <kbd className="hidden sm:inline ml-0.5 text-[9px] font-mono px-1 rounded bg-black/30 border border-current/40 opacity-70 relative">V</kbd>
        </button>
      )}

      {/* T128: stadium-noise warning chip. Surfaced only when the last
          recording's noise floor exceeded the threshold. Tells the
          operator their environment will degrade STT accuracy — no
          recovery action from us, just an honest heads-up. */}
      {/* T132: offline-queue size pill. Reassures the operator that
          their utterances aren't lost during a network blip — they're
          locally stashed and will replay when connectivity returns. */}
      {ptt.supported && ptt.queuedCount > 0 && (
        <span
          className="hidden md:inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md border border-sky-500/40 bg-sky-500/10 text-sky-200"
          title={`${ptt.queuedCount} utterance${ptt.queuedCount === 1 ? "" : "s"} queued offline — will retry when online.`}
        >
          📡 Queued: {ptt.queuedCount}
        </span>
      )}
      {ptt.supported && ptt.isNoisy && (
        <span
          className="hidden md:inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-200"
          title={t("voice.noisy_tooltip")}
        >
          <AlertTriangle className="w-3 h-3" /> {t("voice.noisy")} ({(ptt.noiseFloor * 100).toFixed(0)}%)
        </span>
      )}
      {/* 2026-05-29 — Status text + EN language switch + AI fallback
          button removed from the inline row to save horizontal space
          (founder ask: clean 2-row top header). Status visibility
          still lives on the PTT mic button (red while recording,
          amber while transcribing); AI fallback is still toggleable
          via the help modal; lang switch is rarely used. */}

      {onReplayTour && (
        <button
          type="button"
          onClick={onReplayTour}
          aria-label="Replay voice scoring tutorial"
          className="hidden md:flex items-center gap-1 px-2 py-1 rounded-md text-[10px] uppercase tracking-wider font-semibold bg-purple-500/20 border border-purple-400/50 text-purple-200 hover:bg-purple-500/30 hover:text-purple-100 transition-colors"
          title="Replay the voice scoring tutorial — walk-through of every command, with audio samples (🔊) for each"
        >
          <GraduationCap className="w-3 h-3" />
          Tutorial
        </button>
      )}
      {/* 2026-05-29 — Noise gate moved OUT of VoiceScoringPanel and into
          the spatial tab strip (next to the wagon-wheel button) per
          founder ask. Keeping the button definition below guarded behind
          a non-existent `_renderNoiseGateInVoicePanel` flag so it can be
          re-enabled with a one-line change if needed. */}
      {false && ptt.supported && typeof ptt.setNoiseGateEnabled === "function" && (
        <button
          type="button"
          onClick={() => ptt.setNoiseGateEnabled(!ptt.noiseGateEnabled)}
          aria-pressed={!!ptt.noiseGateEnabled}
          title={
            ptt.noiseGateEnabled
              ? "Noise gate ON — high-pass + compressor before Whisper. Click to disable if your voice is quiet."
              : "Noise gate OFF — raw mic audio. Click to re-enable filtering."
          }
          className={`hidden md:inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-md border transition-colors ${
            ptt.noiseGateEnabled
              ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20"
              : "border-bg-secondary bg-bg-primary/60 text-text-secondary hover:text-text-primary"
          }`}
        >
          🛡 Noise gate {ptt.noiseGateEnabled ? "ON" : "OFF"}
        </button>
      )}
      {onCalibrate && (
        <button
          type="button"
          onClick={onCalibrate}
          aria-label="Run 60-second voice calibration"
          className="hidden md:flex items-center gap-1 px-2 py-1 rounded-md text-[10px] uppercase tracking-wider font-semibold bg-emerald-500/15 border border-emerald-400/40 text-emerald-200 hover:bg-emerald-500/25 transition-colors"
          title="60-second voice test — read 8 prompts, see your recognition accuracy. Run it once on every new device."
        >
          <Gauge className="w-3 h-3" />
          Calibrate
        </button>
      )}
      {/* 2026-05-29 — Help icon removed from the inline row (founder ask).
          Voice command help still accessible via VoiceTutorialModal /
          Tutorial button above. */}

      {/* Auto-confirm toast for AI-fallback suggestions. Renders as a
          floating banner anchored to the voice panel. 2-second
          countdown; auto-commits unless the operator taps ✕ to cancel
          (or ✓ to apply early). Founder ask 2026-05-25: don't let an
          AI misparse commit silently. */}
      {pendingApply && (
        <div
          // top-28 (~7rem) clears the sticky match-state header (h-9)
          // PLUS the stack of editing/lock warning bars (top-9 strips
          // that may render simultaneously). z-[55] keeps it above the
          // top-20 strikeToast / aiToast. UI audit 2026-05-26.
          className="fixed top-28 left-1/2 -translate-x-1/2 z-[55] flex items-center gap-2 px-3 py-2 rounded-lg border border-purple-400/60 bg-purple-950/95 backdrop-blur-sm shadow-2xl text-xs"
          role="status"
        >
          <Sparkles className="w-3.5 h-3.5 text-purple-300 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-purple-300 flex items-center gap-2">
              <span>AI suggests — confirms in {Math.ceil(pendingMsLeft / 1000)}s</span>
              {typeof pendingApply.confidence === "number" && (
                <span className={`px-1.5 py-0.5 rounded font-mono normal-case tracking-normal ${
                  pendingApply.confidence >= 0.8 ? "bg-emerald-500/20 text-emerald-300" :
                  pendingApply.confidence >= 0.65 ? "bg-amber-500/20 text-amber-300" :
                  "bg-rose-500/20 text-rose-300"
                }`}>
                  {Math.round(pendingApply.confidence * 100)}%
                </span>
              )}
            </div>
            <div className="text-purple-50 truncate" title={pendingApply.label}>
              {pendingApply.label}
            </div>
          </div>
          <button
            type="button"
            onClick={confirmPending}
            className="flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-500/20 border border-emerald-400/50 text-emerald-200 text-[10px] uppercase tracking-wider hover:bg-emerald-500/30"
            title="Apply now (skip the 2s wait)"
          >
            <Check className="w-3 h-3" /> Apply
          </button>
          <button
            type="button"
            onClick={cancelPending}
            className="flex items-center gap-1 px-2 py-1 rounded-md bg-rose-500/20 border border-rose-400/50 text-rose-200 text-[10px] uppercase tracking-wider hover:bg-rose-500/30"
            title="Discard the AI suggestion"
          >
            <X className="w-3 h-3" /> Cancel
          </button>
        </div>
      )}

      {/* Recent-utterances strip — last 5 applied actions for at-a-glance
          mishear detection. Hides itself when there are no applied items
          yet (clean state). Founder ask 2026-05-26 ("recent utterances
          strip"). */}
      {recentApplied && recentApplied.length > 1 && (
        <div className="hidden md:flex items-center gap-1 max-w-[260px] overflow-x-auto whitespace-nowrap text-[10px] text-text-secondary border-l border-bg-secondary pl-2">
          <span className="uppercase tracking-wider text-[9px] opacity-60 shrink-0">recent:</span>
          {recentApplied.slice(1, 5).map((r, i) => (
            <span
              key={`${r.ts}-${i}`}
              className="px-1.5 py-0.5 rounded bg-bg-secondary/60 border border-bg-secondary truncate max-w-[100px]"
              title={r.label}
            >
              {r.label}
            </span>
          ))}
        </div>
      )}

      {helpOpen && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
          onClick={() => setHelpOpen(false)}
        >
          <div
            className="bg-bg-primary rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto border border-bg-secondary shadow-2xl p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4 sticky top-0 bg-bg-primary -mx-5 px-5 pb-2 border-b border-bg-secondary z-10">
              <h3 className="text-base font-semibold flex items-center gap-2">
                <Mic className="w-4 h-4" /> Voice commands cheat sheet
              </h3>
              <button onClick={() => setHelpOpen(false)} className="text-text-secondary hover:text-text-primary" aria-label="Close">✕</button>
            </div>

            <Section title="How to talk">
              <Row k="Hold V" v="Push-to-talk Whisper — recommended. Speak while held, release to transcribe." />
              <Row k="Shift+V" v="Repeat the last audio through Whisper without re-speaking." />
              <Row k="🎧 Hands-free" v="Tap once, speak naturally — silence auto-submits each utterance." />
              <Row k='"no, I said X"' v="Undo last + re-apply with corrected phrase." />
            </Section>

            <Section title="Runs">
              <Row k="dot / no run" v="0 runs" />
              <Row k="one / single" v="1 run" />
              <Row k="two / couple" v="2 runs" />
              <Row k="three" v="3 runs" />
              <Row k="four / boundary" v="4 + boundary" />
              <Row k="six / maximum" v="6 + boundary" />
              <Row k="chauka / chakka" v="4 / 6 (Hindi)" />
            </Section>

            <Section title="Extras">
              <Row k="wide / WD" v="toggle wide" />
              <Row k="no ball / NB" v="toggle no-ball" />
              <Row k="bye / B / leg bye / LB" v="toggle byes" />
              <Row k="free hit / FH" v="toggle free hit" />
              <Row k="dead ball" v="toggle dead ball" />
              <Row k="short run / SR" v="mark short run" />
              <Row k="quick run / QR" v="mark quick running" />
            </Section>

            <Section title="Wickets">
              <Row k="wicket / out" v="open dismissal picker" />
              <Row k="bowled / caught / lbw" v="+ wicket type" />
              <Row k="run out / stumped" v="+ wicket type" />
              <Row k="caught Bumrah" v="+ fielder" />
            </Section>

            <Section title="Actions">
              <Row k="save / save ball" v="commit the ball" />
              <Row k="undo / undo ball" v="revert last ball" />
              <Row k="redo / redo ball" v="re-apply undone ball" />
              <Row k="cancel" v="cancel pending save" />
              <Row k="clip in / clip out" v="mark video clip boundaries" />
              <Row k="edit ball" v="open last ball editor" />
              <Row k="DRS / review" v="open dismissal/review picker" />
              <Row k="QC" v="navigate to per-ball QC page" />
              <Row k="clear ball / reset" v="wipe in-progress ball (keeps bowler+batter)" />
            </Section>

            <Section title="Spatial nav">
              <Row k="pitch map" v="switch to pitch matrix" />
              <Row k="height impact" v="switch to impact matrix" />
              <Row k="wagon wheel" v="switch to wagon picker" />
            </Section>

            <Section title="Scope-locked phrasing">
              <Row k="stroke cover drive" v="only sets Stroke" />
              <Row k="delivery slider" v="only sets Delivery type" />
              <Row k="pitch yorker outside off" v="pitch + line" />
              <Row k="impact chest middle stump" v="height + impact line" />
              <Row k="wagon deep cover" v="wagon position" />
              <Row k="bounce kept low" v="only Bounce" />
              <Row k="keeper up to stumps" v="only Keeper" />
            </Section>

            <Section title="Player swaps">
              <Row k="next bowler Bumrah" v="set new bowler" />
              <Row k="Kohli on strike" v="set striker" />
              <Row k="keeper Pant" v="set keeper" />
            </Section>

            <p className="text-[10px] text-text-secondary mt-4 leading-snug border-l-2 border-purple-400/40 pl-2">
              <strong className="text-text-primary">Privacy note:</strong>{" "}
              Whisper PTT uploads each utterance to our backend → Groq
              (≤25 sec audio), discarded after transcription. The audio
              is also archived to S3 alongside the ball so QC can play it
              back if scoring needs review. Continuous mode (if ever
              enabled) runs in-browser only.
            </p>
            <p className="text-[10px] text-text-secondary mt-2 italic">
              Browser support: Chrome / Edge ✓ · Safari (recent versions ✓) · Firefox ✗
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
